import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/menu.jsx';
import { ThemeProvider } from '@/hooks/useTheme.jsx';
import { ToastProvider } from '@/hooks/useToast.jsx';
import { AuthProvider } from '@/routes/AuthProvider.jsx';
import { App } from './App.jsx';
import { STALE_TIME } from '@/lib/constants.js';
import './index.css';

/**
 * The query client, configured around the API's rate limit.
 *
 * The backend allows 100 requests per 15 minutes per caller by default. A panel
 * with a dozen tables that refetched on every window focus would burn through
 * that during a normal morning, and the operator would start seeing 429s on
 * work they did not initiate. So:
 *
 *   - `refetchOnWindowFocus: false` — alt-tabbing is not a data event.
 *   - `staleTime: 30s` — the same view revisited within half a minute is served
 *     from cache. Nothing here moves faster than that except the payout job,
 *     which runs once a minute.
 *   - **no polling anywhere.** Where a screen wants fresher data it offers a
 *     refresh button; the two screens that do refresh on their own (System,
 *     Dashboard) use a 60-second interval, matching the server-side cache.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,

      /**
       * One retry, and never on a 4xx.
       *
       * Retrying a 401 fights the refresh interceptor; retrying a 403 or a 422
       * is asking the same wrong question again. Only a network blip or a 5xx
       * is worth a second attempt.
       */
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },

      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },

    mutations: {
      // A failed write is never retried automatically. Several of these are
      // non-idempotent from the operator's point of view — a repeated status
      // change writes a second audit row — and a silent retry of something that
      // may in fact have succeeded is the wrong default.
      retry: false,
    },
  },
});

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <TooltipProvider delayDuration={200}>
            <ToastProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </ToastProvider>
          </TooltipProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
